/**
 * Knižnica kariet (docs/design/3-kniznica.png): prieskumník balíčkov všetkých
 * postáv. Obsah je celý na klientovi, takže to nestojí žiadny request.
 *
 * Vľavo balíček vybranej postavy s filtrom podľa typu a hodnoty, vpravo
 * inšpektor karty — veľká karta a vysvetlenie, čo robí a kedy.
 */

import type { CardDef, HeroDef } from '@unmatched/engine';
import { content, localizeCard, localizeHero } from '@unmatched/content';
import { EFFECTS, IMPLEMENTED_HEROES } from '@unmatched/engine';
import { useEffect, useMemo, useState } from 'react';

import { Card } from '../ui/Card.js';
import { CardInfo } from '../ui/CardInfo.js';
import { CardStyleToggle } from '../ui/CardStyleToggle.js';
import { artOf } from './Home.js';

interface Props {
  heroId: string | null;
  onHero: (heroId: string) => void;
}

const TYPES: { type: CardDef['type']; label: string }[] = [
  { type: 'attack', label: 'Útok' },
  { type: 'defense', label: 'Obrana' },
  { type: 'versatile', label: 'Univerzál' },
  { type: 'scheme', label: 'Schéma' },
];

const VALUES = ['1', '2', '3', '4', '5+'] as const;

function heroList(): HeroDef[] {
  return Object.values(content.heroes)
    .filter((hero) => content.decks[hero.deckId]?.cards.length)
    .sort(
      (a, b) =>
        Number(IMPLEMENTED_HEROES.includes(b.id)) - Number(IMPLEMENTED_HEROES.includes(a.id)) ||
        a.name.localeCompare(b.name),
    );
}

export function Library({ heroId, onHero }: Props) {
  const heroes = useMemo(heroList, []);
  const hero = heroes.find((h) => h.id === heroId) ?? heroes[0] ?? null;
  const deck = hero ? content.decks[hero.deckId] : undefined;

  const [query, setQuery] = useState('');
  const [types, setTypes] = useState<Set<CardDef['type']>>(new Set());
  const [value, setValue] = useState<(typeof VALUES)[number] | null>(null);
  const [heroQuery, setHeroQuery] = useState('');
  const [selected, setSelected] = useState<CardDef | null>(null);

  // Pri zmene postavy začne inšpektor jej prvou kartou.
  useEffect(() => {
    setSelected(deck?.cards[0] ?? null);
  }, [deck]);

  const cards = deck?.cards ?? [];
  const total = cards.reduce((sum, card) => sum + card.qty, 0);

  const stats = useMemo(() => {
    const byType = new Map<CardDef['type'], number>();
    for (const card of cards) byType.set(card.type, (byType.get(card.type) ?? 0) + card.qty);
    const offence = cards.filter((c) => c.type === 'attack' || c.type === 'versatile');
    const weighted = (list: CardDef[], pick: (c: CardDef) => number) => {
      const count = list.reduce((sum, c) => sum + c.qty, 0);
      return count ? list.reduce((sum, c) => sum + pick(c) * c.qty, 0) / count : 0;
    };
    return {
      byType,
      avgAttack: weighted(offence, (c) => c.value),
      avgBoost: weighted(cards, (c) => c.boost),
    };
  }, [cards]);

  const shown = cards.filter((card) => {
    if (types.size && !types.has(card.type)) return false;
    if (value) {
      if (card.type === 'scheme') return false;
      if (value === '5+' ? card.value < 5 : card.value !== Number(value)) return false;
    }
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const text = localizeCard(card, 'en');
    return [text.name, ...Object.values(text.effects)].join(' ').toLowerCase().includes(needle);
  });

  const heroMatches = heroes.filter((h) =>
    heroQuery ? h.name.toLowerCase().includes(heroQuery.trim().toLowerCase()) : true,
  );

  function toggleType(type: CardDef['type']) {
    setTypes((current) => {
      const next = new Set(current);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  if (!hero) return <p className="muted">Knižnica je prázdna.</p>;
  const ready = IMPLEMENTED_HEROES.includes(hero.id);
  const special = localizeHero(hero, 'en').special;
  const owner = (card: CardDef) =>
    card.character
      ? `Iba ${
          card.character === hero.id
            ? hero.name
            : (hero.sidekicks?.find((s) => s.id === card.character)?.name ?? card.character)
        }`
      : 'Ktokoľvek';

  return (
    <div className="library">
      <div className="crumbs">
        <span>Knižnica & archív</span> / <b>Prieskumník balíčkov ({hero.name})</b>
        <span className="crumbs__right">
          <span className="tag">{total} kariet v balíčku</span>
          <CardStyleToggle />
        </span>
      </div>

      <section className="panel library__filters">
        <input
          className="search"
          type="search"
          value={query}
          placeholder="Hľadať kartu podľa názvu alebo efektu…"
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="filterChips">
          <button
            type="button"
            className={`filterChip ${types.size === 0 ? 'is-active' : ''}`}
            onClick={() => setTypes(new Set())}
          >
            Všetky typy
          </button>
          {TYPES.map(({ type, label }) => (
            <button
              key={type}
              type="button"
              className={`filterChip filterChip--${type} ${types.has(type) ? 'is-active' : ''}`}
              onClick={() => toggleType(type)}
            >
              <span className="typeDot" /> {label}
            </button>
          ))}
        </div>
        <div className="filterChips filterChips--values">
          <span className="eyebrow">Boj:</span>
          {VALUES.map((v) => (
            <button
              key={v}
              type="button"
              className={`valueChip ${value === v ? 'is-active' : ''}`}
              onClick={() => setValue(value === v ? null : v)}
            >
              {v}
            </button>
          ))}
        </div>
      </section>

      <section className="panel heroStrip">
        <header className="heroStrip__head">
          <h2 className="panel__title panel__title--sm">Hrdinovia</h2>
          <span className="heroStrip__count">
            {heroMatches.length} z {heroes.length}
          </span>
          <input
            className="search search--sm heroStrip__search"
            type="search"
            value={heroQuery}
            placeholder="Hľadať hrdinu…"
            onChange={(event) => setHeroQuery(event.target.value)}
          />
        </header>
        <div className="heroStrip__list">
          {heroMatches.map((h) => (
            <button
              key={h.id}
              type="button"
              className={`heroChip ${h.id === hero.id ? 'is-active' : ''}${
                IMPLEMENTED_HEROES.includes(h.id) ? '' : ' is-wip'
              }`}
              onClick={() => onHero(h.id)}
            >
              <span className="heroChip__art">
                {artOf(h.id) ? (
                  <img src={artOf(h.id)} alt="" loading="lazy" draggable={false} />
                ) : null}
              </span>
              <span className="heroChip__text">
                <b>{h.name}</b>
                <small>
                  {h.attack === 'melee' ? 'zblízka' : 'na diaľku'} · {h.hp} HP
                  {IMPLEMENTED_HEROES.includes(h.id) ? '' : ' · WIP'}
                </small>
              </span>
            </button>
          ))}
        </div>
      </section>

      <div className="library__body">
        <div className="library__main">
          <section className="panel deckHead">
            <h1 className="deckHead__title">
              Balíček: {hero.name}
              {hero.sidekicks?.length
                ? ` & ${hero.sidekicks.map((s) => `${s.count > 1 ? `${s.count} ` : ''}${s.name}`).join(', ')}`
                : ''}
              <span className={`tag ${total === 30 ? 'tag--primary' : ''}`}>
                {total === 30 ? 'Kompletný (30/30)' : `${total}/30`}
              </span>
              {ready ? (
                <span className="tag tag--ok">Efekty hotové</span>
              ) : (
                <span className="tag tag--wip">WIP</span>
              )}
            </h1>
            {hero.blurb ? <p className="muted">{hero.blurb}</p> : null}
            <div className="deckHead__stats">
              <span className="miniStat">
                <small>Priem. útok</small>
                <b>{stats.avgAttack.toFixed(1)}</b>
              </span>
              <span className="miniStat">
                <small>Priem. boost</small>
                <b>{stats.avgBoost.toFixed(1)}</b>
              </span>
              <span className="miniStat">
                <small>Zdravie</small>
                <b>{hero.hp}</b>
              </span>
              <span className="miniStat">
                <small>Pohyb</small>
                <b>{hero.move}</b>
              </span>
            </div>
            <div className="typeBar" aria-hidden="true">
              {TYPES.map(({ type }) =>
                stats.byType.get(type) ? (
                  <span
                    key={type}
                    className={`typeBar__seg typeBar__seg--${type}`}
                    style={{ flex: stats.byType.get(type) }}
                  />
                ) : null,
              )}
            </div>
            <div className="typeLegend">
              {TYPES.map(({ type, label }) =>
                stats.byType.get(type) ? (
                  <span key={type} className={`typeLegend__item typeLegend__item--${type}`}>
                    <span className="typeDot" /> {stats.byType.get(type)}× {label} (
                    {Math.round(((stats.byType.get(type) ?? 0) / (total || 1)) * 100)} %)
                  </span>
                ) : null,
              )}
            </div>
          </section>

          {shown.length === 0 ? (
            <p className="muted">Filtru nezodpovedá žiadna karta.</p>
          ) : (
            <div className="libGrid">
              {shown.map((card) => (
                <div
                  key={card.id}
                  className={`libCard libCard--${card.type} ${selected?.id === card.id ? 'is-selected' : ''}`}
                >
                  <header className="libCard__head">
                    <span className="libCard__owner">{owner(card)}</span>
                    <span className="libCard__qty">{card.qty}× v balíčku</span>
                    <span className="libCard__boost" title="boost">
                      +{card.boost}
                    </span>
                  </header>
                  <Card def={card} large onClick={() => setSelected(card)} />
                  <footer className="libCard__foot">
                    <span>{TYPES.find((t) => t.type === card.type)?.label}</span>
                    {EFFECTS[card.id] ? (
                      <span className="libCard__ok">efekt funguje ✓</span>
                    ) : (
                      <span>len hodnota</span>
                    )}
                  </footer>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="library__side">
          <section className="panel inspector">
            <header className="panel__head">
              <span className="panel__title panel__title--sm">
                <span className="dot dot--primary" /> Inšpektor karty
              </span>
            </header>
            {selected ? (
              <>
                <div className="inspector__card">
                  <Card def={selected} huge />
                </div>
                <CardInfo def={selected} />
              </>
            ) : (
              <p className="muted">Klikni na kartu.</p>
            )}
          </section>

          {special ? (
            <section className="panel">
              <header className="panel__head">
                <span className="panel__title panel__title--sm">Špeciálna schopnosť</span>
              </header>
              <p className="special">{special}</p>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
